import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import { z } from 'zod'
import {
  statementShape,
  statementFilterShape,
  statementRowShape,
  confirmResponseShape,
} from './models'

const listRows = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: z.array(z.object(statementRowShape)),
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.get(`${slashEndingBaseUri}${input}/rows/`)
    return fromApi(response.data)
  },
})

const confirm = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rows: z
      .array(
        z.object({
          id: z.string(),
          action: z.enum(['approve', 'reject']),
          autofilledFields: z.record(z.string(), z.any()).optional(),
        }),
      )
      .optional(),
  },
  outputShape: confirmResponseShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi, toApi } }) => {
    const body = input.rows ? { rows: toApi(input.rows) } : {}
    const response = await client.post(`${slashEndingBaseUri}${input.statementId}/confirm/`, body)
    return fromApi(response.data)
  },
})

const confirmRow = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowId: z.string().uuid(),
    autofilledFields: z.record(z.string(), z.any()).optional(),
  },
  outputShape: {
    status: z.string(),
    expenseId: z.string().nullable().optional(),
  },
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const body = input.autofilledFields ? { autofilled_fields: input.autofilledFields } : {}
    const response = await client.post(
      `${slashEndingBaseUri}${input.statementId}/confirm-row/${input.rowId}/`,
      body,
    )
    return fromApi(response.data)
  },
})

const saveDrafts = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: {
    created: z.array(z.string()),
  },
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/save-drafts/`)
    return fromApi(response.data)
  },
})

export async function exportCsv(statementId: string): Promise<Blob> {
  const response = await axiosInstance.get(`/expenses/statements/${statementId}/export/`, {
    responseType: 'blob',
  })
  return response.data as Blob
}

const reconcile = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: statementShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/reconcile/`)
    return fromApi(response.data)
  },
})

const columnPreview = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: {
    columns: z.array(
      z.object({
        rawHeader: z.string(),
        mappedTo: z.string().nullable(),
        sample: z.string(),
      }),
    ),
  },
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.get(`${slashEndingBaseUri}${input}/column-preview/`)
    return fromApi(response.data)
  },
})

const confirmColumns = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    columnMap: z.record(z.string(), z.string()),
  },
  outputShape: statementShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(
      `${slashEndingBaseUri}${input.statementId}/confirm-columns/`,
      { column_map: input.columnMap },
    )
    return fromApi(response.data)
  },
})

const linkRow = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowId: z.string().uuid(),
    expenseId: z.string().uuid(),
  },
  outputShape: statementRowShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(
      `${slashEndingBaseUri}${input.statementId}/rows/${input.rowId}/link/`,
      { expense_id: input.expenseId },
    )
    return fromApi(response.data)
  },
})

const unlinkRow = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowId: z.string().uuid(),
  },
  outputShape: statementRowShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(
      `${slashEndingBaseUri}${input.statementId}/rows/${input.rowId}/unlink/`,
    )
    return fromApi(response.data)
  },
})

const reclassifyRow = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowId: z.string().uuid(),
    classification: z.enum(['unmatched']),
  },
  outputShape: statementRowShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.patch(
      `${slashEndingBaseUri}${input.statementId}/rows/${input.rowId}/reclassify/`,
      { classification: input.classification },
    )
    return fromApi(response.data)
  },
})

const approveRow = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowId: z.string().uuid(),
  },
  outputShape: statementRowShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(
      `${slashEndingBaseUri}${input.statementId}/rows/${input.rowId}/approve/`,
    )
    return fromApi(response.data)
  },
})

const rejectRow = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowId: z.string().uuid(),
  },
  outputShape: statementRowShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(
      `${slashEndingBaseUri}${input.statementId}/rows/${input.rowId}/reject/`,
    )
    return fromApi(response.data)
  },
})

const bulkApprove = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowIds: z.array(z.string().uuid()),
  },
  outputShape: z.array(z.object(statementRowShape)),
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input.statementId}/bulk-approve/`, {
      row_ids: input.rowIds,
    })
    return fromApi(response.data)
  },
})

const bulkReject = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    rowIds: z.array(z.string().uuid()),
  },
  outputShape: z.array(z.object(statementRowShape)),
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input.statementId}/bulk-reject/`, {
      row_ids: input.rowIds,
    })
    return fromApi(response.data)
  },
})

const confirmReview = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: statementShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/confirm-review/`)
    return fromApi(response.data)
  },
})

const setReport = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    reportId: z.string().uuid().nullable(),
  },
  outputShape: statementShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.patch(`${slashEndingBaseUri}${input.statementId}/set-report/`, {
      suggested_report: input.reportId,
    })
    return fromApi(response.data)
  },
})

// PATCH the statement's account; the server marks it confirmed.
const setAccount = createCustomServiceCall({
  inputShape: {
    statementId: z.string().uuid(),
    paymentAccountId: z.string().uuid(),
  },
  outputShape: statementShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.patch(`${slashEndingBaseUri}${input.statementId}/`, {
      payment_account: input.paymentAccountId,
    })
    return fromApi(response.data)
  },
})

const reopenReview = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: statementShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/reopen-review/`)
    return fromApi(response.data)
  },
})

export const statementApi = createApi({
  client: axiosInstance,
  baseUri: '/expenses/statements/',
  models: {
    entity: statementShape,
    extraFilters: statementFilterShape,
  },
  customCalls: {
    listRows,
    confirm,
    confirmRow,
    confirmReview,
    reopenReview,
    setReport,
    setAccount,
    linkRow,
    unlinkRow,
    reconcile,
    columnPreview,
    confirmColumns,
    reclassifyRow,
    approveRow,
    rejectRow,
    bulkApprove,
    bulkReject,
    saveDrafts,
  },
})
